import { DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Typo from '@/components/Typography/Typo';
import service from '@/services/index';
import usePopupStore from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';

import useGlobalSearchModalAction from '../hooks/useGlobalSearchModalAction';
import { SEARCH_RESULT_STATUS } from '../types/searchConst';

type Props = {
  selected: boolean;
  history: GlobalSearchResult;
  onClick?: (alias: string) => void;
};

const SearchHistoryItem = (props: Props) => {
  const addToast = useToast();
  const { refreshHistoryList } = useGlobalSearchModalAction();
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));

  const onClickDeleteSearchHistory = async (e: any) => {
    e.stopPropagation();
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete search history',
      message: 'Are you sure you want to delete this search history?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        deleteSearchHistory();
      },
    });
  };

  const deleteSearchHistory = async () => {
    try {
      await service.globalSearch.deleteSearchHistoryItem(props.history.alias);
      addToast('Search history deleted', 'success');
      refreshHistoryList();
    } catch (e) {
      addToast('Failed to delete search history', 'fail');
    }
  };

  const deleteDisabled =
    props.history.semantic_result_status !== SEARCH_RESULT_STATUS.DONE;

  return (
    <Style
      onClick={() => props.onClick?.(props.history.alias)}
      selected={props.selected}
      className="group"
    >
      <Typo type="label2" weight="r" className="text-gray-800" ellipsis>
        {props.history.title}
      </Typo>
      <IconButton
        icon={<DeleteOutlined />}
        outlined={false}
        disabled={deleteDisabled}
        onClick={onClickDeleteSearchHistory}
        className="opacity-0 group-hover:opacity-100 absolute right-2 top-2 transition-opacity duration-200"
        tooltip={
          deleteDisabled ? 'Cannot be deleted while in progress' : 'Delete'
        }
      />
    </Style>
  );
};

const Style = styled(VFlex)<{ selected: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 12px 12px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.selected ? props.theme.colors.gray200 : 'transparent'};
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) =>
    props.selected ? props.theme.colors.primary100 : 'transparent'};
  transition: all 0.2s;
  padding-right: 32px;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
  }
`;

export default SearchHistoryItem;
